/*!
 * Copyright © 2019-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { isElementNull } from '../../../../utils';
import {
  DeviceHealthIssueCategories,
  EPlotIcon,
  EPlotType,
  ESignalStatus,
  ESpeedLimitTypes,
  ESpeedType,
  ETrackingDeviceEventCode,
  EVehicleFuelType,
  EVehicleStatusType,
  IUserSettings,
  IVehiclePlot
} from '../../../core/models';
import { VehiclePlotTimerFormatService } from '../../../core/services/vehicle-plot-timer-format';
import { IDeviceHealth } from '../../../core/store/state/device-health.state';
import { IVehicleStatusInfo } from '../../models';

@Component({
  selector: 'fm-vehicle-status-item',
  templateUrl: './vehicle-status-item.component.html',
  styleUrls: ['./vehicle-status-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VehicleStatusItemComponent implements OnChanges {
  @Input()
  vehicle: IVehiclePlot;
  @Input()
  vehiclePlotsTimer: number;
  @Input()
  vehicleStatusInfo: IVehicleStatusInfo;
  @Input()
  isHighlighted: boolean;
  @Input()
  vehicleFuelType: EVehicleFuelType;
  @Input()
  userSettings: IUserSettings;
  @Input()
  isEVChangesEnabled: boolean;
  @Input()
  isSelected: boolean;
  @Input()
  vehicleHealthStatus: IDeviceHealth;

  @Output()
  contextMenuClicked = new EventEmitter<MouseEvent>();

  @HostBinding('class.vehicle-status--highlighted')
  get highlight() {
    return this.isHighlighted;
  }

  @HostBinding('class.selected')
  get selected(): boolean {
    return this.isSelected;
  }

  @HostBinding('class.critical-health')
  get criticalHealth(): boolean {
    return this.vehicleStatusInfo?.showHealthStatus && this.checkVehicleHealthIssue();
  }

  @HostBinding('class.vehicle-address-ready')
  vehicleAddressReady = false;

  eventCodeShutDown = ETrackingDeviceEventCode.ShutDown;

  EPlotType = EPlotType;
  EPlotIcon = EPlotIcon;
  EVehicleStatusType = EVehicleStatusType;
  ESignalStatus = ESignalStatus;
  ESpeedType = ESpeedType;
  ESpeedLimitTypes = ESpeedLimitTypes;
  EVehicleFuelType = EVehicleFuelType;
  chargeDurationSeconds: number;
  formattedDuration: string;
  showEVTag: boolean;

  isStoppedOrIdle: boolean;
  isCharging = false;
  trimmedDriverName: string;

  private _measurementCanvas: HTMLCanvasElement;

  private readonly _stoppedOrIdleStatuses = <EPlotIcon[]>[
    EPlotIcon.Stopped,
    EPlotIcon.Idle,
    EPlotIcon.StoppedPoweredAsset,
    EPlotIcon.IdlePoweredAsset
  ];

  constructor(private readonly _timerFormatService: VehiclePlotTimerFormatService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['vehicle']) {
      this.isStoppedOrIdle = this._stoppedOrIdleStatuses.includes(this.vehicle.iconClass);
      this._isVehicleAddressReady();

      if (this.isEVChangesEnabled) {
        this._setChargingState();
      }

      this.trimmedDriverName = this.vehicle.driverDisplayName ? this.vehicle.driverDisplayName.trim() : '';
    }
    this._formatPlotTimer();
    this.showEVTag = this.vehicleFuelType === EVehicleFuelType.Electric || !!this.vehicle.evBatteryData;
  }

  public openVehicleContextMenuClick($event: MouseEvent): void {
    this.contextMenuClicked.emit($event);
  }

  public checkVehicleHealthIssue(): boolean {
    if (this.vehicleHealthStatus) {
      return DeviceHealthIssueCategories.findIndex(x => x === this.vehicleHealthStatus?.healthStatus.toUpperCase()) > -1;
    }
    return false;
  }

  public isTextOverflowing(text: string, overflowWidth: number): boolean {
    // Tried to implement this logic passing in the span elementRef and comparing scrollWidth with offsetWidth  but it didn't work with
    // virtual scrolling
    // re-use canvas object for better performance
    const canvas = this._measurementCanvas || (this._measurementCanvas = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    context.font = '12px neue-haas-grotesk-text';
    const metrics = context.measureText(text);
    return metrics.width > overflowWidth;
  }

  private _isVehicleAddressReady() {
    this.vehicleAddressReady = this.vehicle.address && this.vehicle.address.length > 0;
  }

  private _setChargingState(): void {
    if (!isElementNull(this.vehicle.evBatteryData) && this.vehicle.evBatteryData?.chargingState) {
      if (this.vehicle.status === EVehicleStatusType.Charging) {
        this.isCharging = true;
      }
    } else {
      this.isCharging = false;
    }
  }

  private _formatPlotTimer(): void {
    this.formattedDuration = this._timerFormatService.format(this.vehicle.duration, this.vehiclePlotsTimer);
  }
}
